import { useRef, useState, useEffect } from "react";

/**
 * Tracks which items changed between data refreshes and returns a Set of
 * their keys so the UI can briefly highlight the changed rows/cards.
 *
 * - Skips the very first render (initial load) so the page doesn't flash
 *   everything on mount.
 * - The returned `changedKeys` Set auto-clears after `duration` ms.
 *
 * @param data      Current data array
 * @param keyFn     Extracts a unique key from each item (e.g. item => item.id)
 * @param hashFn    Produces a string hash that captures the fields you care about
 * @param duration  How long the highlight stays (ms, default 1500)
 *
 * @returns Set<string> of keys whose hash changed since last render
 *
 * Usage:
 *   const changedIds = useChangeHighlight(
 *     offers?.items ?? [],
 *     item => item.id,
 *     item => `${item.statusCode}-${item.totalEstimatedCost}`,
 *   );
 *   // Then on the row: style={changedIds.has(item.id) ? { animation: 'rowHighlight 1.5s ease-out' } : undefined}
 */
export const useChangeHighlight = <T>(
  data: T[],
  keyFn: (item: T) => string,
  hashFn: (item: T) => string,
  duration: number = 1500
): Set<string> => {
  const isFirstRender = useRef(true);
  const prevHashMap = useRef<Map<string, string>>(new Map());
  const [changedKeys, setChangedKeys] = useState<Set<string>>(new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Build current hash map
    const currentMap = new Map<string, string>();
    data.forEach((item) => {
      currentMap.set(keyFn(item), hashFn(item));
    });

    if (isFirstRender.current) {
      // First render — store hashes but don't highlight anything
      isFirstRender.current = false;
      prevHashMap.current = currentMap;
      return;
    }

    // Compare with previous
    const changed = new Set<string>();
    currentMap.forEach((hash, key) => {
      const prevHash = prevHashMap.current.get(key);
      // Highlight if the item existed before AND its hash changed
      if (prevHash !== undefined && prevHash !== hash) {
        changed.add(key);
      }
      // Also highlight new items that appeared
      if (prevHash === undefined) {
        changed.add(key);
      }
    });

    prevHashMap.current = currentMap;

    if (changed.size > 0) {
      setChangedKeys(changed);

      // Clear after duration
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        setChangedKeys(new Set());
        timerRef.current = null;
      }, duration);
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [data, duration]);

  return changedKeys;
};

export default useChangeHighlight;
